/**
 * Tipos e validação dos Mini Formulários, compartilhados entre backend e
 * frontend. A validação roda nos dois lados: no cliente (feedback imediato na
 * página pública) e no servidor (fonte de verdade ao submeter — nunca confiar
 * apenas no cliente).
 */

import { isValidCPF, isValidCNPJ } from "./index";

export type CampoTipo =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "datetime"
  | "telefone"
  | "cpf"
  | "cnpj"
  | "email"
  | "agendamento"
  | "lista_opcoes"
  | "multipla_escolha"
  | "arquivo"
  | "carrinho";

/** Filtro de tipo aceito por um campo `arquivo`. */
export type TipoArquivoFiltro = "imagem" | "video" | "audio" | "documento" | "qualquer";

/** Metadados de um arquivo enviado, gravados em `valores` após a submissão. */
export type ArquivoValor = {
  id: number;
  filename: string;
  mime_type: string;
  tamanho: number;
  /** Caminho no storage (ArquivosFactory) — só existe no lado servidor. */
  caminho?: string;
};

const PREFIXO_MIME_POR_FILTRO: Record<"imagem" | "video" | "audio", string> = {
  imagem: "image/",
  video: "video/",
  audio: "audio/",
};

/**
 * Verifica se um mimetype respeita o filtro configurado no campo. "documento"
 * é definido por exclusão (nem imagem, nem vídeo, nem áudio) em vez de uma
 * lista fixa de mimetypes de documento, que nunca seria exaustiva.
 */
export function arquivoCorrespondeFiltro(mimeType: string, filtro?: TipoArquivoFiltro): boolean {
  if (!filtro || filtro === "qualquer") return true;
  if (filtro === "documento") {
    return !mimeType.startsWith("image/") && !mimeType.startsWith("video/") && !mimeType.startsWith("audio/");
  }
  return mimeType.startsWith(PREFIXO_MIME_POR_FILTRO[filtro]);
}

/** Valor do atributo `accept` do `<input type="file">` para cada filtro. */
export const ACCEPT_ARQUIVO_POR_FILTRO: Record<TipoArquivoFiltro, string> = {
  imagem: "image/*",
  video: "video/*",
  audio: "audio/*",
  documento: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rtf",
  qualquer: "*/*",
};

/** Como renderizar `lista_opcoes` na página pública: seletor (padrão) ou radio buttons. */
export type EstiloListaOpcoes = "select" | "radio";

/**
 * Config de um campo de agendamento. A fonte de horários é abstrata: pode vir
 * da agenda nativa (RepoAgenda) ou de uma Integration (templates/custom). A
 * config vive dentro do jsonb `campos`; nunca é exposta ao cliente na página
 * pública (a busca de horários passa por um proxy no backend).
 */
export type AgendamentoAgenda = {
  fonte: "agenda";
  /** ID da agenda interna a consultar. */
  id_agenda: number;
  /** Dias à frente a consultar (default 30). */
  janela_dias?: number;
};

export type AgendamentoIntegration = {
  fonte: "integration";
  integrationId: number;
  /** "custom" ou a chave do template. */
  template_key: string;
  template_version?: string;
  /** Ação da integração que lista os horários. */
  action: string;
  /** Parâmetros fixos passados à ação. */
  paramsFixos?: Record<string, string>;
  /** JSONPath do array de itens no resultado. */
  jsonpathLista: string;
  /** JSONPath do datetime de cada item (relativo ao item). */
  jsonpathDatetime: string;
  /** JSONPath do id opaco de cada item (relativo ao item). */
  jsonpathSlotId?: string;
};

export type AgendamentoConfig = AgendamentoAgenda | AgendamentoIntegration;

/**
 * Config de um campo `carrinho`. Como a de agendamento, nunca é exposta ao
 * cliente na página pública: a listagem de itens passa por um proxy no backend.
 */
export type CarrinhoConfig = {
  /** Catálogo de onde saem os itens e os preços. */
  catalogo_chave: string;
  /** Máximo de linhas distintas que o cliente pode montar. */
  max_itens?: number;
};

/**
 * Linha do carrinho, como o cliente a submete. É só isto que vai ao servidor —
 * preço e total são recalculados lá, a partir do catálogo. Nada de valor
 * monetário vindo do navegador entra no orçamento.
 */
export type CarrinhoItemValor = {
  codigo: string;
  quantidade: number;
};

/**
 * O que fica gravado em `valores` depois da submissão: o snapshot do orçamento
 * que o servidor calculou.
 */
export type CarrinhoValor = {
  orcamento_id: number;
  token: string;
  moeda: string;
  total: number;
  itens: { codigo: string; nome: string; quantidade: number; subtotal: number }[];
  texto: string;
};

export type ValidadorTipo =
  | "obrigatorio"
  | "min_len"
  | "max_len"
  | "min"
  | "max"
  | "regex";

export type Validador = {
  tipo: ValidadorTipo;
  /** Parâmetro do validador (tamanho, valor limite, padrão regex). */
  valor?: string | number;
  /** Mensagem de erro customizada (opcional). */
  mensagem?: string;
};

export type MiniFormularioCampo = {
  nome: string;
  tipo: CampoTipo;
  label: string;
  placeholder?: string;
  ajuda?: string;
  ordem?: number;
  validadores?: Validador[];
  /** Config exclusiva de campos `tipo === "agendamento"`. */
  agendamento?: AgendamentoConfig;
  /** Lista de opções (texto livre) — usada por `lista_opcoes` e `multipla_escolha`. */
  opcoes?: string[];
  /** Estilo de exibição de `lista_opcoes` (default "select"). */
  estilo_lista?: EstiloListaOpcoes;
  /** Filtro de tipo aceito — exclusiva de campos `tipo === "arquivo"`. */
  filtro_arquivo?: TipoArquivoFiltro;
  /** Config exclusiva de campos `tipo === "carrinho"`. */
  carrinho?: CarrinhoConfig;
};

/** Erros de validação por nome de campo. */
export type ErrosValidacao = Record<string, string>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function estaVazio(valor: unknown): boolean {
  if (valor == null) return true;
  if (typeof valor === "string") return valor.trim() === "";
  if (Array.isArray(valor)) return valor.length === 0;
  return false;
}

/**
 * Valida um único campo. Retorna a mensagem de erro (string) ou `null` se OK.
 * Campos vazios só falham no validador `obrigatorio` — os demais são ignorados
 * quando não há valor.
 */
export function validarCampo(campo: MiniFormularioCampo, valor: unknown): string | null {
  const validadores = campo.validadores ?? [];
  const vazio = estaVazio(valor);

  // Estrutural: o valor precisa vir das opções definidas (não confiar apenas
  // no que a página pública oferece — o cliente pode submeter qualquer coisa).
  if (!vazio && campo.tipo === "lista_opcoes") {
    const opcoes = campo.opcoes ?? [];
    if (!opcoes.includes(String(valor))) return "Opção inválida";
  }
  if (!vazio && campo.tipo === "multipla_escolha") {
    const opcoes = campo.opcoes ?? [];
    const selecionadas = Array.isArray(valor) ? valor : [valor];
    if (!selecionadas.every((v) => opcoes.includes(String(v)))) return "Opção inválida";
  }
  if (!vazio && campo.tipo === "cpf") {
    if (!isValidCPF(String(valor))) return "CPF inválido";
  }
  if (!vazio && campo.tipo === "cnpj") {
    if (!isValidCNPJ(String(valor))) return "CNPJ inválido";
  }
  if (!vazio && campo.tipo === "email") {
    if (!EMAIL_RE.test(String(valor))) return "E-mail inválido";
  }

  // Carrinho: só a FORMA é validada aqui. Preço, desconto e total não passam
  // por esta função em momento algum — o servidor os calcula a partir do
  // catálogo ao submeter, ignorando qualquer valor que o cliente mande junto.
  if (!vazio && campo.tipo === "carrinho") {
    if (!Array.isArray(valor)) return "Carrinho inválido";

    const max = campo.carrinho?.max_itens;
    if (max && valor.length > max) return `Escolha no máximo ${max} itens`;

    for (const linha of valor as any[]) {
      if (!linha?.codigo) return "Item sem identificação no carrinho";
      const qtd = Number(linha.quantidade);
      if (!Number.isFinite(qtd) || qtd <= 0) return "Informe uma quantidade maior que zero";
    }
  }

  for (const v of validadores) {
    const msg = v.mensagem;
    switch (v.tipo) {
      case "obrigatorio":
        if (vazio) return msg ?? "Campo obrigatório";
        break;
      case "min_len":
        if (!vazio && String(valor).length < Number(v.valor))
          return msg ?? `Mínimo de ${v.valor} caracteres`;
        break;
      case "max_len":
        if (!vazio && String(valor).length > Number(v.valor))
          return msg ?? `Máximo de ${v.valor} caracteres`;
        break;
      case "min":
        if (!vazio && Number(valor) < Number(v.valor))
          return msg ?? `Valor mínimo é ${v.valor}`;
        break;
      case "max":
        if (!vazio && Number(valor) > Number(v.valor))
          return msg ?? `Valor máximo é ${v.valor}`;
        break;
      case "regex":
        if (!vazio && v.valor != null) {
          try {
            if (!new RegExp(String(v.valor)).test(String(valor)))
              return msg ?? "Formato inválido";
          } catch {
            // Regex mal configurado: ignora em vez de quebrar a validação.
          }
        }
        break;
    }
  }

  return null;
}

/**
 * Valida todos os campos contra um objeto de valores. Retorna um mapa de
 * `{ [nomeCampo]: mensagem }` apenas com os campos que falharam (vazio = válido).
 */
export function validarFormulario(
  campos: MiniFormularioCampo[],
  valores: Record<string, unknown>
): ErrosValidacao {
  const erros: ErrosValidacao = {};
  for (const campo of campos) {
    const erro = validarCampo(campo, valores?.[campo.nome]);
    if (erro) erros[campo.nome] = erro;
  }
  return erros;
}
